/**
 * @file The site's stylesheet graph — every stylesheet the static
 *       pages reference, imported in one place.
 * @module @labcraft/www/ui/styles
 *
 * The page components live only in the server render graph (the SSG
 * entry), so their CSS imports never reach the client build on their
 * own. This module closes that gap: the islands entry imports it and
 * the client build emits one CSS bundle from it; every rendered page
 * links that bundle. CSS-module files resolve to the same hashed
 * class names on both sides (same file, same Vite config), so the
 * server-rendered markup and the emitted CSS agree.
 *
 * The `.module.css` files are imported WITH bindings and re-exported
 * through {@link PAGE_STYLE_SHEETS}, which the islands bootloader
 * consumes. This is load-bearing, not ceremony: a side-effect-only
 * `import './x.module.css'` is tree-shaken out of the production
 * build (CSS modules are JS modules whose unused exports mark the
 * whole sheet removable), which silently drops every page-level
 * stylesheet from the bundle — the un-styled-site failure mode this
 * replaced. Plain (non-module) sheets keep bare imports; those carry
 * side effects and are never shaken.
 *
 * ADD AN ENTRY (import + array element) when a new page-level
 * stylesheet lands — a missing one shows up as an unstyled section in
 * the PR's screenshot gate.
 */

import { LabCraftLogo } from '@labcraft/ui'
import '@labcraft/ui/global.css'
import './global.css'
import './blog/blog.css'
import topNav from './top_nav.module.css'
import footer from './footer.module.css'
import themeToggle from './theme_toggle.module.css'
import landing from './landing.module.css'
import manifesto from './manifesto.module.css'
import contact from './contact.module.css'
import privacy from './privacy.module.css'
import notFound from './not_found.module.css'
import terminalFrame from './terminal_frame.module.css'
import captcha from './captcha.module.css'
import fromTheBlog from './blog/from_the_blog.module.css'
import heroEyebrow from './hero_eyebrow.module.css'

/**
 * Every page-level CSS-module class map, referenced so the client
 * build retains the sheets (see the file JSDoc for why a bare
 * side-effect import is not enough).
 */
export const PAGE_STYLE_SHEETS: readonly Record<string, string>[] = [
  topNav,
  footer,
  themeToggle,
  landing,
  manifesto,
  contact,
  privacy,
  notFound,
  terminalFrame,
  captcha,
  fromTheBlog,
  heroEyebrow,
]

/**
 * Kit components that render server-side only (no island uses them),
 * kept referenced here so their own CSS modules ride the client
 * bundle. The kit's export map doesn't expose raw stylesheet paths,
 * so referencing the component is the supported way to carry its CSS.
 * Currently just the `lc>` logo (nav + footer brand mark).
 */
export const KIT_SERVER_COMPONENTS: readonly unknown[] = [LabCraftLogo]
